import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ShieldAlert, Clock, Trash2, Undo2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeleteAccount() {
  const [policy, setPolicy] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);
  const [support, setSupport] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/account/deletion-policy`).then(r => setPolicy(r.data)).catch(() => {});
    axios.get(`${API}/api/site-content?lang=es`)
      .then(r => setSupport(r.data?.contact_us || r.data?.contact || null))
      .catch(() => {});
  }, []);

  const asText = (d) => (typeof d === 'string' ? d : Array.isArray(d)
    ? d.map(e => e?.msg || JSON.stringify(e)).join(' ') : d?.msg || 'Error inesperado');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const login = await axios.post(`${API}/api/auth/login`, { email, password });
      const token = login.data.token;
      const res = await axios.post(`${API}/api/account/deletion-request`,
        { password, confirm: true, client: 'web', reason: 'web_delete_account_page' },
        { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data.request);
    } catch (err) {
      setError(asText(err.response?.data?.detail) || 'No pudimos procesar la solicitud');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800" data-testid="delete-account-page">
      <div className="mx-auto max-w-3xl px-5 py-14 sm:py-20">
        <Link to="/" className="text-sm text-[#1B84FF] hover:text-[#0F6FDB]" data-testid="delete-account-home-link">
          ← AvisoCar
        </Link>

        <h1 className="mt-8 text-4xl font-semibold tracking-tight sm:text-5xl">
          Eliminar tu cuenta
        </h1>
        <p className="mt-4 max-w-2xl text-base text-slate-500">
          Puedes pedir la eliminación de tu cuenta AvisoCar desde aquí o desde la app.
          Te explicamos exactamente qué pasa antes de que confirmes.
        </p>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="delete-account-step-immediate">
            <ShieldAlert className="h-5 w-5 text-[#1B84FF]" />
            <h2 className="mt-3 text-base font-medium">Al confirmar</h2>
            <p className="mt-2 text-sm text-slate-500">
              Cerramos tus sesiones, desactivamos las notificaciones de todos tus
              dispositivos y pausamos los avisos de tus QR.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="delete-account-step-grace">
            <Clock className="h-5 w-5 text-[#1B84FF]" />
            <h2 className="mt-3 text-base font-medium">
              {policy ? `${policy.grace_days} días para cambiar de opinión` : 'Periodo de gracia'}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Puedes cancelar la solicitud desde la app o volviendo a esta página.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="delete-account-step-final">
            <Trash2 className="h-5 w-5 text-[#1B84FF]" />
            <h2 className="mt-3 text-base font-medium">Después</h2>
            <p className="mt-2 text-sm text-slate-500">
              Borramos tus perfiles, mensajes y notificaciones, y anonimizamos tu cuenta.
              Tus QR impresos quedan retirados de forma permanente y nunca se reasignan.
            </p>
          </div>
        </div>

        {policy?.qr_policy && (
          <p className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-700/90"
             data-testid="delete-account-qr-policy">
            {policy.qr_policy}
          </p>
        )}

        {status ? (
          <div className="mt-10 rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-6"
               data-testid="delete-account-success">
            <h2 className="text-lg font-medium text-emerald-700">Solicitud registrada</h2>
            <p className="mt-2 text-sm text-slate-600">
              Estado: <strong data-testid="delete-account-status">{status.status}</strong>
              {status.scheduled_for && <> · se completará a partir del {new Date(status.scheduled_for).toLocaleDateString('es-PE')}</>}
            </p>
            <p className="mt-3 flex items-center gap-2 text-sm text-slate-500">
              <Undo2 className="h-4 w-4" /> Puedes cancelarla desde tu panel mientras esté en curso.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-4 rounded-2xl border border-slate-200 bg-white p-6"
                data-testid="delete-account-form">
            <h2 className="text-lg font-medium">Confirma que eres tú</h2>
            <input
              type="email" required value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Tu correo de AvisoCar" autoComplete="email"
              className="w-full rounded-xl border border-slate-700 bg-[#F8FAFC] px-4 py-3 text-sm outline-none focus:border-emerald-500"
              data-testid="delete-account-email-input"
            />
            <input
              type="password" required value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Tu contraseña" autoComplete="current-password"
              className="w-full rounded-xl border border-slate-700 bg-[#F8FAFC] px-4 py-3 text-sm outline-none focus:border-emerald-500"
              data-testid="delete-account-password-input"
            />
            <label className="flex items-start gap-3 text-sm text-slate-600">
              <input type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)}
                     className="mt-1 h-4 w-4" data-testid="delete-account-confirm-checkbox" />
              Entiendo que perderé el acceso a mis perfiles protegidos y que mis QR impresos
              quedarán retirados.
            </label>
            {error && (
              <p className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-700"
                 data-testid="delete-account-error">{error}</p>
            )}
            <button
              type="submit" disabled={!confirm || busy}
              className="inline-flex items-center gap-2 rounded-full bg-red-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="delete-account-submit-button"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Solicitar eliminación
            </button>
          </form>
        )}

        <div className="mt-10 text-sm text-slate-500" data-testid="delete-account-support">
          ¿Necesitas ayuda? Escríbenos
          {support?.email ? <> a <a className="text-[#1B84FF]" href={`mailto:${support.email}`}>{support.email}</a></> : ' desde la página de contacto'}
          {' '}o revisa la{' '}
          <Link to="/privacidad" className="text-[#1B84FF]">política de privacidad</Link>.
        </div>
      </div>
    </div>
  );
}
